import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../atoms/button";
import ToastContext from "../../context/toastProvider";
import { STORES_URL } from "../../context/paths";
import { statusObject } from "../utils/allProductStatus";
import { getProductsForTable, searchProductIds } from "./functions";
import { buildSelectionColumn, PRODUCT_BASE_COLUMNS } from "./productsColumns";
import DeliveryMenu from "./DeliveryMenu";
import { selectIdRange, toggleId } from "./productSelectionRange";
import SearchInput from "../filters/SearchInput";
import FilterChips from "../filters/FilterChips";
import ActiveFiltersBar from "../filters/ActiveFiltersBar";
import { countPreset, DEFAULT_PRODUCT_STATUSES, describeStatusSelection, matchStatusPreset, PRODUCT_STATUS_PRESETS } from "../filters/statusPresets";
import { buildActiveFilterItems, describeFilterValue, getFilterValue, setFilterValue } from "../filters/columnFilterState";
import { describeDeadlineFilter } from "../filters/deadlineFilter";
import { useUrlColumnFilters } from "../filters/useUrlColumnFilters";
import DataTable from "../table/DataTable";
import { useDataTable } from "../table/useDataTable";
import { TOOLBAR_BUTTON } from "../table/styles";
import { isExtendedArt, KIND_FILTER_OPTIONS, KIND_LABELS, splitIdsByKind } from "../extendedArt/kind";

// `kind` (Tipo) mora na URL como os outros filtros, mas é filtro de servidor: não vira filtro de coluna.
const KIND_KEY = "kind";
const FILTER_SCHEMA = {
    ...Object.fromEntries(
        PRODUCT_BASE_COLUMNS.filter((column) => column.filter && column.filter !== "ids").map((column) => [column.key, column.filter])
    ),
    [KIND_KEY]: "list",
};
const EXPLICIT_ALL = ["status"];
const STATUS_DESCRIBE = { status: (value) => describeStatusSelection(PRODUCT_STATUS_PRESETS, statusObject, value) };
const DEADLINE_KEY = "calculatedDeadline";

const fetchStores = async (token) => {
    try {
        const response = await fetch(STORES_URL, {
            method: "GET",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        return data.type === "success" ? data.data : [];
    } catch {
        return [];
    }
};

const Products = ({ user }) => {
    const { badToast } = useContext(ToastContext);
    const location = useLocation();
    const navigate = useNavigate();
    const presetStatus = location.state?.presetStatus;

    // Atalhos antigos (state.presetStatus) continuam valendo; a Home nova usa a query string.
    const getDefaultFilters = useCallback(
        () => [
            { id: "status", value: Array.isArray(presetStatus) && presetStatus.length ? presetStatus : DEFAULT_PRODUCT_STATUSES },
        ],
        [presetStatus]
    );
    const { filters, q, setFilters, setSearch, clearAll, initialSorting } = useUrlColumnFilters({ schema: FILTER_SCHEMA, explicitAll: EXPLICIT_ALL, getDefaultFilters });
    const kind = getFilterValue(filters, KIND_KEY)?.[0] ?? "";

    const [products, setProducts] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [stores, setStores] = useState([]);
    const [select, setSelect] = useState(false);
    const [gradingList, setGradingList] = useState([]);
    const [qInput, setQInput] = useState(q);
    const [searchIds, setSearchIds] = useState(null);
    const [searchFailed, setSearchFailed] = useState(false);
    const anchorIdRef = useRef(null);
    const badToastRef = useRef(badToast);
    badToastRef.current = badToast;
    const tableRef = useRef(null);

    const loadSeqRef = useRef(0);
    const loadProducts = async (kindValue = kind) => {
        // Trocas rápidas de Tipo: só a última busca vale.
        const seq = ++loadSeqRef.current;
        setLoading(true);
        const data = await getProductsForTable(user.token, kindValue || undefined);
        if (seq !== loadSeqRef.current) return;
        setLoading(false);
        if (data.type === "success") {
            setProducts(data.data ?? []);
            setLoadError(null);
        } else {
            setLoadError(data.message || "Erro ao buscar pedidos!");
            badToast("Erro ao buscar pedidos!");
        }
    };

    useEffect(() => {
        if (location.state?.origin === "users-new") {
            // Fluxo antigo do modal: hoje a criação vive na página própria.
            navigate("/manafix/products/create", { state: location.state });
            return;
        }
        fetchStores(user.token).then(setStores);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Trocar o Tipo refaz a busca no servidor com `kind`.
    useEffect(() => {
        if (location.state?.origin === "users-new") return;
        loadProducts(kind);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind]);

    // O backend devolve `store` como id (string) nas linhas estendidas; a coluna "Loja" lê store.name.
    const rows = useMemo(
        () => products.map((item) => (isExtendedArt(item) && typeof item.store === "string"
            ? { ...item, store: { _id: item.store, name: stores.find((s) => s._id === item.store)?.name } }
            : item)),
        [products, stores]
    );

    useEffect(() => {
        setQInput(q);
    }, [q]);

    useEffect(() => {
        if (!q) {
            setSearchIds(null);
            setSearchFailed(false);
            return undefined;
        }
        let cancelled = false;
        setSearchIds(null);
        setSearchFailed(false);
        searchProductIds(user.token, q).then((ids) => {
            if (cancelled) return;
            if (ids === null) {
                // Falha não pode parecer "nada encontrado": avisa e não aplica o filtro por ids.
                setSearchFailed(true);
                badToastRef.current("Erro ao buscar pedidos!");
            } else {
                setSearchIds(ids);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [q, user.token]);

    useEffect(() => {
        if (!select) anchorIdRef.current = null;
    }, [select]);

    const applySelectionClick = useCallback((id, shiftKey) => {
        if (!id) return;
        if (shiftKey && anchorIdRef.current != null) {
            const visibleIds = (tableRef.current?.getRowModel().rows ?? []).map((row) => row.original._id);
            setGradingList((current) => selectIdRange(visibleIds, anchorIdRef.current, id, current));
        } else {
            setGradingList((current) => toggleId(current, id));
        }
        anchorIdRef.current = id;
    }, []);

    const onToggle = useCallback((event, id) => {
        event.stopPropagation();
        applySelectionClick(id, event.shiftKey);
    }, [applySelectionClick]);

    const handleRowClick = useCallback((row, event) => {
        const target = event?.target;
        if (target?.closest?.("a") || target?.closest?.('input[type="checkbox"]')) return;
        applySelectionClick(row._id, Boolean(event?.shiftKey));
    }, [applySelectionClick]);

    const columns = useMemo(
        () => (select ? [buildSelectionColumn({ isSelected: (id) => gradingList.includes(id), onToggle }), ...PRODUCT_BASE_COLUMNS] : PRODUCT_BASE_COLUMNS),
        [select, gradingList, onToggle]
    );

    const columnFilters = useMemo(() => {
        const tableFilters = filters.filter((filter) => filter.id !== KIND_KEY);
        return q && searchIds ? [...tableFilters, { id: "_id", value: { term: q, ids: searchIds } }] : tableFilters;
    }, [filters, q, searchIds]);
    const columnFiltersRef = useRef(columnFilters);
    columnFiltersRef.current = columnFilters;
    const kindRef = useRef(kind);
    kindRef.current = kind;
    const onColumnFiltersChange = useCallback(
        (updater) => {
            const next = typeof updater === "function" ? updater(columnFiltersRef.current) : updater;
            // A tabela não conhece o Tipo: devolve o `kind` para não sumir da URL.
            setFilters(setFilterValue(next, KIND_KEY, kindRef.current ? [kindRef.current] : undefined));
        },
        [setFilters]
    );
    const applyKind = (value) => setFilters((current) => setFilterValue(current, KIND_KEY, value ? [value] : undefined));

    const { table, resetLayout } = useDataTable({ tableKey: "admin-products", columns, data: rows, columnFilters, onColumnFiltersChange, initialSorting });
    tableRef.current = table;

    const statusFacets = table.getColumn("status")?.getFacetedUniqueValues();
    const presetOptions = PRODUCT_STATUS_PRESETS.map((preset) => ({ ...preset, count: countPreset(preset, statusFacets) }));
    const selectedPresetKey = matchStatusPreset(PRODUCT_STATUS_PRESETS, getFilterValue(filters, "status"));
    const applyPreset = (preset) => {
        setFilters((current) => setFilterValue(current, "status", preset.statuses ?? undefined));
        // Carrinhos: os mais recentes primeiro. Os outros chips não mexem na ordenação.
        if (preset.key === "carrinhos") table.setSorting([{ id: "createdAt", desc: true }]);
    };

    const deliveryValue = getFilterValue(filters, "delivery");
    const applyDelivery = (option) =>
        setFilters((current) => {
            const next = setFilterValue(current, "delivery", option.key ? [option.key] : undefined);
            return option.key === "presencial" ? next : setFilterValue(next, "store.name", undefined);
        });
    const storeValue = getFilterValue(filters, "store.name")?.[0] ?? "";
    const applyStore = (name) => setFilters((current) => setFilterValue(current, "store.name", name ? [name] : undefined));

    const submitSearch = () => {
        if (!qInput.trim()) {
            clearSearch();
            return;
        }
        setSearch(qInput, { clear: ["status"] });
    };
    const clearSearch = () => {
        setQInput("");
        setSearch("");
    };

    const describeMap = {
        ...STATUS_DESCRIBE,
        [DEADLINE_KEY]: (value) => describeDeadlineFilter(value) ?? describeFilterValue({}, value),
    };
    const activeItems = buildActiveFilterItems(table, { exclude: ["_id"], describe: describeMap });
    if (kind) activeItems.unshift({ id: KIND_KEY, label: `Tipo: ${KIND_LABELS[kind] ?? kind}`, onRemove: () => applyKind("") });
    if (q) activeItems.unshift({ id: "q", label: `Busca: "${q}"`, onRemove: clearSearch });

    const resultCount = table.getFilteredRowModel().rows.length;
    const searching = Boolean(q) && searchIds === null && !searchFailed;

    // A seleção pode misturar os dois produtos (spec §5.1): o cadastro de serial comum só recebe grading.
    // Só conta o que está na lista carregada: trocar o Tipo refaz a busca e pode tirar pedidos já selecionados.
    const selection = useMemo(() => {
        const loadedIds = new Set(products.map((row) => row._id));
        return splitIdsByKind(gradingList.filter((id) => loadedIds.has(id)), products);
    }, [gradingList, products]);

    const goToRegisterSerials = () => {
        if (!selection.grading.length) return;
        navigate("/manafix/serials/register", { state: { origin: "products", products: selection.grading } });
    };
    const goToTagGenerator = () => {
        if (!selection.grading.length && !selection.extended.length) return;
        navigate("/manafix/serials/tags", { state: { origin: "products", products: selection.grading, extendedOrders: selection.extended } });
    };

    const resultText = !loading && !searching && !searchFailed
        ? (resultCount === 1 ? "1 pedido encontrado" : `${resultCount.toLocaleString("pt-BR")} pedidos encontrados`)
        : null;

    return (
        // --table-offset: altura do que fica acima e abaixo da tabela (linhas 1 e 2, toolbar e paginação).
        <main className="text-left" style={{ "--table-offset": "150px" }}>
            <div className="flex flex-col gap-1 border-b border-primary/60 px-2 py-1">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-base font-semibold text-laranjinha">Pedidos</h1>
                    <SearchInput
                        value={qInput}
                        onChange={setQInput}
                        onSearch={submitSearch}
                        onClear={clearSearch}
                        placeholder="Buscar por nome, e-mail, CPF, rastreio ou ID do pedido…"
                    />
                    <Button classes="bg-blue-300 hover:bg-blue-400 inline whitespace-nowrap text-sm text-black" click={() => navigate("/manafix/products/create", { state: null })}>Criar pedido</Button>
                    <Button classes="bg-purple-200 hover:bg-purple-300 inline whitespace-nowrap text-sm text-black" click={() => navigate("/manafix/extended-art/create", { state: null })}>Novo pedido de arte estendida</Button>
                    {select ? (
                        <div className="flex items-center gap-2">
                            <Button classes="bg-red-300 hover:bg-red-400 inline text-sm text-black" click={() => setSelect(false)}>Cancelar</Button>
                            <Button disabled={!selection.grading.length} classes="bg-green-300 hover:bg-green-400 inline whitespace-nowrap text-sm text-black" click={goToRegisterSerials}>Avaliar pedidos</Button>
                            <Button disabled={!selection.grading.length && !selection.extended.length} classes="bg-purple-300 hover:bg-purple-400 inline whitespace-nowrap text-sm text-black" click={goToTagGenerator}>Gerar etiquetas</Button>
                        </div>
                    ) : (
                        <Button classes="bg-purple-300 hover:bg-purple-400 inline whitespace-nowrap text-sm text-black" click={() => setSelect(true)}>Selecionar pedidos</Button>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <FilterChips className="min-w-0 flex-1" ariaLabel="Filtros rápidos de status" options={presetOptions} selectedKey={selectedPresetKey} onSelect={applyPreset} />
                    <label className="ml-auto flex shrink-0 items-center gap-1 text-sm text-laranjinha/80">Tipo:
                        <select aria-label="Tipo de pedido" className="rounded border border-primary/40 bg-mgs-surface px-1 py-0.5 text-laranjinha" value={kind} onChange={(event) => applyKind(event.target.value)}>
                            {KIND_FILTER_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </label>
                    <div className="shrink-0">
                        <DeliveryMenu deliveryValue={deliveryValue} storeValue={storeValue} stores={stores} onDelivery={applyDelivery} onStore={applyStore} />
                    </div>
                </div>
            </div>

            {loadError && !products.length ? (
                <p className="m-8 text-center text-red-600">{loadError}</p>
            ) : (
                <DataTable
                    table={table}
                    resetLayout={resetLayout}
                    loading={loading || searching}
                    onRowClick={select ? handleRowClick : undefined}
                    rowClassName={select ? () => "cursor-pointer" : undefined}
                    exportable={user.roles.includes("master")}
                    exportFileName="pedidos"
                    toolbarStart={
                        <>
                            <ActiveFiltersBar items={activeItems} onClearAll={() => { setQInput(""); clearAll(); }} />
                            {resultText ? <span className="whitespace-nowrap text-xs text-laranjinha/50">{resultText}</span> : null}
                        </>
                    }
                    toolbarEnd={
                        <button type="button" className={TOOLBAR_BUTTON} aria-label="Atualizar" title="Atualizar" onClick={() => loadProducts()} disabled={loading}>
                            ⟳
                        </button>
                    }
                />
            )}
        </main>
    );
};

export default Products;
